import React, { useState, useEffect } from 'react';
import { getProducts, createProduct, updateProduct, deleteProduct, generatePrefix, checkPrefix, generateImage, improvePrompt, listGallery, saveToGallery, deleteFromGallery } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import CertificatePreview from '../components/CertificatePreview';
import RichTextEditor from '../components/RichTextEditor';
import {
    Certificate,
    Plus,
    Pencil,
    Trash,
    Eye,
    Palette,
    Image,
    TextT,
    ListBullets,
    CurrencyDollar,
    MapPin,
    Bed,
    Check,
    X,
    MagicWand,
    SpinnerGap,
    Tag,
    Calendar,
    TextAa,
    Sparkle,
    FloppyDisk,
    Images
} from '@phosphor-icons/react';

const FONT_OPTIONS = [
    { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans (Predeterminada)' },
    { value: 'Inter', label: 'Inter (Limpia)' },
    { value: 'Cormorant Garamond', label: 'Cormorant Garamond (Elegante)' },
    { value: 'Playfair Display', label: 'Playfair Display (Premium)' },
    { value: 'Manrope', label: 'Manrope (Moderna)' },
    { value: 'Outfit', label: 'Outfit (Geométrica)' },
    { value: 'Merriweather', label: 'Merriweather (Clásica)' },
    { value: 'Lora', label: 'Lora (Profesional)' },
    { value: 'Fraunces', label: 'Fraunces (Editorial)' }
];

const BORDER_STYLES = [
    { value: 'elegant', label: 'Elegante', desc: 'Esquinas redondeadas con borde sutil' },
    { value: 'modern', label: 'Moderno', desc: 'Borde navy fuerte' },
    { value: 'minimal', label: 'Minimal', desc: 'Sin bordes notables' },
    { value: 'voucher', label: 'Cupón', desc: 'Borde punteado tipo voucher' }
];

const LAYOUT_STYLES = [
    { value: 'certificate', label: 'Certificado Formal', desc: 'Estilo diploma premium' },
    { value: 'voucher', label: 'Voucher/Cupón', desc: 'Estilo Groupon, enfocado en oferta' },
    { value: 'card', label: 'Tarjeta', desc: 'Compacto, moderno' }
];

const WEIGHT_OPTIONS = [
    { value: 300, label: 'Light (300)' },
    { value: 400, label: 'Regular (400)' },
    { value: 500, label: 'Medium (500)' },
    { value: 600, label: 'Semibold (600)' },
    { value: 700, label: 'Bold (700)' },
    { value: 800, label: 'Extrabold (800)' },
    { value: 900, label: 'Black (900)' }
];

const DEFAULT_DESIGN = {
    background_image: '',
    background_color: '#FFFFFF',
    title_font: 'Plus Jakarta Sans',
    title_color: '#1F2D4B',
    subtitle_font: 'Inter',
    subtitle_color: '#4B5878',
    body_font: 'Inter',
    label_font: 'Inter',
    accent_color: '#FB7185',
    badge_color: '#1F2D4B',
    border_style: 'elegant',
    layout_style: 'certificate',
    title_size: 28,
    subtitle_size: 14,
    body_size: 12,
    label_size: 10,
    title_weight: 700,
    subtitle_weight: 400,
    body_weight: 400,
    label_weight: 700,
    title_italic: false,
    subtitle_italic: false,
    body_italic: false,
    label_italic: false
};

const ProductsPage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [activeTab, setActiveTab] = useState('basic');
    const [generatingImage, setGeneratingImage] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiStyle, setAiStyle] = useState('photo');
    const [aiNegative, setAiNegative] = useState('');
    const [improvingPrompt, setImprovingPrompt] = useState(false);
    const [gallery, setGallery] = useState([]);
    const [showGallery, setShowGallery] = useState(false);
    const [prefixAvailable, setPrefixAvailable] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        code_prefix: '',
        title: '',
        subtitle: '',
        brief_description: '',
        full_description: '',
        terms_and_conditions: '',
        how_to_book: '',
        destination: '',
        hotel_category: '',
        nights: 3,
        includes: [],
        excludes: [],
        price: '',
        original_price: '',
        validity_days: 365,
        design: { ...DEFAULT_DESIGN },
        is_active: true,
        sort_order: 0
    });
    
    // Temp state for includes/excludes input
    const [newInclude, setNewInclude] = useState('');
    const [newExclude, setNewExclude] = useState('');

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await getProducts();
            setProducts(response.data);
        } catch (error) {
            toast.error('Error al cargar productos');
        } finally {
            setLoading(false);
        }
    };

    const handlePrefixChange = async (value) => {
        const prefix = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
        setFormData({ ...formData, code_prefix: prefix });
        
        if (prefix.length >= 2 && (!editingProduct || editingProduct.code_prefix !== prefix)) {
            try {
                const response = await checkPrefix(prefix);
                setPrefixAvailable(response.data.available);
            } catch (error) {
                setPrefixAvailable(null);
            }
        } else {
            setPrefixAvailable(null);
        }
    };

    const suggestPrefix = async () => {
        if (!formData.name) {
            toast.error('Ingresa el nombre del producto primero');
            return;
        }
        try {
            const response = await generatePrefix(formData.name);
            setFormData({ ...formData, code_prefix: response.data.prefix });
            setPrefixAvailable(true);
        } catch (error) {
            toast.error('Error al generar prefijo');
        }
    };

    const handleGenerateImage = async () => {
        const prompt = (aiPrompt || formData.destination || '').trim();
        if (!prompt) {
            toast.error('Describe la imagen que quieres en el campo "Describe la imagen"');
            return;
        }

        setGeneratingImage(true);
        try {
            const response = await generateImage({
                prompt,
                style: aiStyle,
                negative: aiNegative || undefined,
                aspect: '3:4'
            });

            if (response.data.success && response.data.full_image) {
                setFormData({
                    ...formData,
                    design: { ...formData.design, background_image: response.data.full_image }
                });
                toast.success('Imagen generada exitosamente');
            } else {
                toast.error('No se pudo generar la imagen');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al generar imagen');
        } finally {
            setGeneratingImage(false);
        }
    };

    const handleImprovePrompt = async () => {
        const raw = (aiPrompt || '').trim();
        if (!raw) {
            toast.error('Escribe una idea primero (aunque sea corta)');
            return;
        }
        setImprovingPrompt(true);
        try {
            const res = await improvePrompt(raw, aiStyle, aiNegative);
            if (res.data.improved_prompt) {
                setAiPrompt(res.data.improved_prompt);
                toast.success('Prompt mejorado ✨');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No se pudo mejorar el prompt');
        } finally {
            setImprovingPrompt(false);
        }
    };

    const loadGallery = async () => {
        try {
            const res = await listGallery();
            setGallery(res.data || []);
        } catch (err) {
            // silent — gallery is optional
        }
    };

    const handleSaveToGallery = async () => {
        const img = formData.design.background_image;
        if (!img || !img.startsWith('data:')) {
            toast.error('Sólo se pueden guardar imágenes generadas con IA');
            return;
        }
        try {
            const res = await saveToGallery({
                image_data: img,
                prompt: aiPrompt,
                style: aiStyle,
            });
            setGallery([res.data, ...gallery]);
            toast.success('Imagen guardada en tu galería');
        } catch (err) {
            toast.error('No se pudo guardar la imagen');
        }
    };

    const handleUseFromGallery = (item) => {
        setFormData({
            ...formData,
            design: { ...formData.design, background_image: item.image_data }
        });
        if (item.prompt) setAiPrompt(item.prompt);
        if (item.style) setAiStyle(item.style);
        setShowGallery(false);
        toast.success('Imagen aplicada al certificado');
    };

    const handleDeleteFromGallery = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm('¿Eliminar esta imagen de la galería?')) return;
        try {
            await deleteFromGallery(id);
            setGallery(gallery.filter(g => g.id !== id));
            toast.success('Imagen eliminada de la galería');
        } catch (err) {
            toast.error('No se pudo eliminar');
        }
    };

    const addInclude = () => {
        if (newInclude.trim()) {
            setFormData({
                ...formData,
                includes: [...formData.includes, newInclude.trim()]
            });
            setNewInclude('');
        }
    };

    const removeInclude = (index) => {
        setFormData({
            ...formData,
            includes: formData.includes.filter((_, i) => i !== index)
        });
    };

    const addExclude = () => {
        if (newExclude.trim()) {
            setFormData({
                ...formData,
                excludes: [...formData.excludes, newExclude.trim()]
            });
            setNewExclude('');
        }
    };

    const removeExclude = (index) => {
        setFormData({
            ...formData,
            excludes: formData.excludes.filter((_, i) => i !== index)
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.name || !formData.code_prefix || !formData.price) {
            toast.error('Completa los campos obligatorios');
            return;
        }
        
        if (formData.code_prefix.length < 2) {
            toast.error('El código debe tener al menos 2 caracteres');
            return;
        }
        
        try {
            const productData = {
                ...formData,
                price: parseFloat(formData.price),
                original_price: formData.original_price ? parseFloat(formData.original_price) : null,
                nights: parseInt(formData.nights) || 3,
                validity_days: parseInt(formData.validity_days) || 365,
                sort_order: parseInt(formData.sort_order) || 0
            };
            
            if (editingProduct) {
                await updateProduct(editingProduct.id, productData);
                toast.success('Producto actualizado');
            } else {
                await createProduct(productData);
                toast.success('Producto creado');
            }
            
            setShowModal(false);
            resetForm();
            fetchProducts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al guardar');
        }
    };

    useEffect(() => {
        if (showModal) {
            loadGallery();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal]);

    const handleEdit = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name,
            code_prefix: product.code_prefix,
            title: product.title || '',
            subtitle: product.subtitle || '',
            brief_description: product.brief_description || '',
            full_description: product.full_description || '',
            terms_and_conditions: product.terms_and_conditions || '',
            how_to_book: product.how_to_book || '',
            destination: product.destination || '',
            hotel_category: product.hotel_category || '',
            nights: product.nights || 3,
            includes: product.includes || [],
            excludes: product.excludes || [],
            price: product.price?.toString() || '',
            original_price: product.original_price?.toString() || '',
            validity_days: product.validity_days || 365,
            design: product.design ? (() => {
                const merged = { ...DEFAULT_DESIGN, ...product.design };
                // Normalize legacy/string size values that may have been stored as e.g. "2xl"
                const SIZE_DEFAULTS = { title_size: 28, subtitle_size: 14, body_size: 12, label_size: 10 };
                for (const k of Object.keys(SIZE_DEFAULTS)) {
                    const v = merged[k];
                    if (typeof v !== 'number' || isNaN(v)) merged[k] = SIZE_DEFAULTS[k];
                }
                // Normalize weight values
                const WEIGHT_DEFAULTS = { title_weight: 700, subtitle_weight: 400, body_weight: 400, label_weight: 700 };
                for (const k of Object.keys(WEIGHT_DEFAULTS)) {
                    const v = merged[k];
                    if (typeof v !== 'number' || isNaN(v)) merged[k] = WEIGHT_DEFAULTS[k];
                }
                // Normalize boolean italic
                for (const k of ['title_italic', 'subtitle_italic', 'body_italic', 'label_italic']) {
                    merged[k] = !!merged[k];
                }
                // Coerce nullable string fields → '' so <Input value={...}> stays controlled
                for (const k of ['background_image', 'background_color', 'title_color', 'subtitle_color', 'accent_color', 'badge_color', 'border_style', 'layout_style']) {
                    if (merged[k] == null) merged[k] = DEFAULT_DESIGN[k] ?? '';
                }
                // Ensure font fallbacks
                if (!merged.body_font) merged.body_font = merged.subtitle_font || DEFAULT_DESIGN.body_font;
                if (!merged.label_font) merged.label_font = merged.subtitle_font || DEFAULT_DESIGN.label_font;
                return merged;
            })() : { ...DEFAULT_DESIGN },
            is_active: product.is_active,
            sort_order: product.sort_order || 0
        });
        setPrefixAvailable(true);
        setShowModal(true);
    };

    const handleDelete = async (productId) => {
        if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;
        try {
            await deleteProduct(productId);
            toast.success('Producto eliminado');
            fetchProducts();
        } catch (error) {
            toast.error('Error al eliminar');
        }
    };

    const resetForm = () => {
        setEditingProduct(null);
        setPrefixAvailable(null);
        setActiveTab('basic');
        setFormData({
            name: '',
            code_prefix: '',
            title: '',
            subtitle: '',
            brief_description: '',
            full_description: '',
            terms_and_conditions: '',
            how_to_book: '',
            destination: '',
            hotel_category: '',
            nights: 3,
            includes: [],
            excludes: [],
            price: '',
            original_price: '',
            validity_days: 365,
            design: { ...DEFAULT_DESIGN },
            is_active: true,
            sort_order: 0
        });
    };

    const openCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F2D4B]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="products-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-[#ECE3D2]">
                <div>
                    <div className="flex items-center gap-2 text-xs mb-2">
                        <span className="text-[#8B8474]">Catálogo</span>
                        <span className="text-[#C7BCA5]">/</span>
                        <span className="text-[#1F2D4B] font-medium">Productos</span>
                    </div>
                    <h1 className="font-editorial text-3xl sm:text-4xl font-semibold text-[#1F2D4B]">
                        Productos
                    </h1>
                    <p className="text-sm text-[#4B5878] mt-1.5">
                        {products.length} {products.length === 1 ? 'certificado configurado' : 'certificados configurados'}
                    </p>
                </div>
                
                <Button
                    onClick={openCreateModal}
                    data-testid="create-product-btn"
                    className="h-10 px-4 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md font-medium btn-lift"
                    
                >
                    <Plus size={16} weight="bold" className="mr-1.5" />
                    Nuevo producto
                </Button>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {products.length === 0 ? (
                    <div className="col-span-full bg-white border border-dashed border-[#ECE3D2] rounded-lg py-16 text-center">
                        <Certificate size={40} weight="regular" className="mx-auto mb-3 text-[#C7BCA5]" />
                        <h3 className="font-editorial text-xl text-[#1F2D4B] mb-1">Aún no hay productos</h3>
                        <p className="text-sm text-[#4B5878] mb-5">Crea tu primer certificado de viaje para empezar a vender</p>
                        <Button
                            onClick={openCreateModal}
                            className="h-9 px-4 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md text-sm btn-lift"
                            
                        >
                            <Plus size={14} weight="bold" className="mr-1.5" />
                            Crear primer producto
                        </Button>
                    </div>
                ) : (
                    products.map((product) => (
                        <div 
                            key={product.id}
                            className="bg-white border border-[#ECE3D2] rounded-lg overflow-hidden hover:border-[#0F1B3D]/20 hover:shadow-sm transition-all"
                            data-testid={`product-card-${product.id}`}
                        >
                            {/* Product Preview Header - always readable navy gradient */}
                            <div 
                                className="h-40 relative"
                                style={{
                                    backgroundImage: product.design?.background_image 
                                        ? `linear-gradient(to bottom, rgba(15,27,61,0.35), rgba(15,27,61,0.85)), url(${product.design.background_image})`
                                        : `linear-gradient(135deg, #1F2D4B 0%, #2E3F60 60%, #4B5878 130%)`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}
                            >
                                {/* Code Badge */}
                                <div className="absolute top-3 left-3 bg-white/95 text-[#1F2D4B] font-mono-brand font-semibold text-[11px] px-2 py-1 rounded">
                                    {product.code_prefix}
                                </div>
                                
                                {/* Status */}
                                <span className={`absolute top-3 right-3 status-badge ${
                                    product.is_active ? 'status-paid' : 'status-failed'
                                }`}>
                                    {product.is_active ? 'Activo' : 'Inactivo'}
                                </span>
                                
                                {/* Title - always white on dark */}
                                <div className="absolute bottom-0 left-0 right-0 p-4">
                                    <h3 className="font-editorial font-semibold text-white text-lg leading-tight line-clamp-2">
                                        {product.name}
                                    </h3>
                                </div>
                            </div>
                            
                            <div className="p-4">
                                {/* Quick Info */}
                                <div className="flex flex-wrap gap-1.5 mb-3">
                                    <span className="inline-flex items-center gap-1 text-[11px] text-[#4B5878] bg-[#F8F4EC] px-2 py-1 rounded">
                                        <MapPin size={11} weight="bold" />
                                        {product.destination || 'Sin destino'}
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-[11px] text-[#4B5878] bg-[#F8F4EC] px-2 py-1 rounded">
                                        <Bed size={11} weight="bold" />
                                        {product.nights}N
                                    </span>
                                </div>
                                
                                {/* Price */}
                                <div className="flex items-baseline gap-2 mb-4">
                                    <span className="font-editorial text-2xl font-semibold text-[#1F2D4B]">
                                        {formatCurrency(product.price)}
                                    </span>
                                    {product.original_price && product.original_price > product.price && (
                                        <span className="text-xs text-[#8B8474] line-through">
                                            {formatCurrency(product.original_price)}
                                        </span>
                                    )}
                                </div>
                                
                                {/* Actions */}
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleEdit(product)}
                                        data-testid={`edit-product-${product.id}`}
                                        className="flex-1 h-9 rounded-md border-[#ECE3D2] hover:bg-[#F8F4EC] text-xs"
                                    >
                                        <Pencil size={12} className="mr-1.5" />
                                        Editar
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDelete(product.id)}
                                        data-testid={`delete-product-${product.id}`}
                                        className="h-9 px-2.5 rounded-md border-[#ECE3D2] text-[#DC2626] hover:bg-red-50"
                                    >
                                        <Trash size={12} />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Create/Edit Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent 
                    className="!block sm:max-w-6xl !max-h-[95vh] !w-[95vw] !p-0 !gap-0 bg-white border border-[#ECE3D2] rounded-xl overflow-hidden"
                    data-testid="product-modal"
                >
                    <DialogHeader className="p-6 pb-4 border-b border-[#ECE3D2]">
                        <DialogTitle className="font-editorial text-2xl font-semibold text-[#1F2D4B]">
                            {editingProduct ? 'Editar producto' : 'Nuevo producto'}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#4B5878]">
                            Configura todos los detalles · la vista previa se actualiza en tiempo real
                        </DialogDescription>
                    </DialogHeader>
                    
                    <form id="product-form" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-[1fr_360px] overflow-hidden h-[calc(95vh-200px)]">
                        {/* LEFT: Tabs */}
                        <div className="overflow-y-auto min-h-0 min-w-0">
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                            <div className="px-6 pt-4 sticky top-0 bg-white z-10 pb-2 border-b border-[#ECE3D2]">
                                <TabsList className="grid grid-cols-3 w-full bg-[#F8F4EC] rounded-md p-1 h-10">
                                    <TabsTrigger value="basic" className="rounded text-xs data-[state=active]:bg-white data-[state=active]:text-[#1F2D4B] data-[state=active]:shadow-sm text-[#4B5878]">
                                        Básico
                                    </TabsTrigger>
                                    <TabsTrigger value="content" className="rounded text-xs data-[state=active]:bg-white data-[state=active]:text-[#1F2D4B] data-[state=active]:shadow-sm text-[#4B5878]">
                                        Contenido
                                    </TabsTrigger>
                                    <TabsTrigger value="design" className="rounded text-xs data-[state=active]:bg-white data-[state=active]:text-[#1F2D4B] data-[state=active]:shadow-sm text-[#4B5878]">
                                        Diseño
                                    </TabsTrigger>
                                </TabsList>
                            </div>
                            
                            <div className="px-6 py-4">
                                {/* Basic Tab */}
                                <TabsContent value="basic" className="space-y-4 mt-0">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Nombre del Producto *</Label>
                                            <Input
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                placeholder="Ej: Escapada 4 Estrellas Riviera Maya"
                                                data-testid="product-name-input"
                                                className="h-11 rounded-xl border-[#ECE3D2]"
                                            />
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label className="flex items-center justify-between">
                                                <span>Código Único (Prefijo) *</span>
                                                <button
                                                    type="button"
                                                    onClick={suggestPrefix}
                                                    className="text-xs text-[#FB7185] hover:underline"
                                                >
                                                    Sugerir automático
                                                </button>
                                            </Label>
                                            <div className="relative">
                                                <Input
                                                    value={formData.code_prefix}
                                                    onChange={(e) => handlePrefixChange(e.target.value)}
                                                    placeholder="CC, CMO, ER..."
                                                    maxLength={4}
                                                    data-testid="product-prefix-input"
                                                    className={`h-11 rounded-xl border-[#ECE3D2] font-mono uppercase pr-10 ${
                                                        prefixAvailable === true ? 'border-[#2D6A4F]' : 
                                                        prefixAvailable === false ? 'border-[#D90429]' : ''
                                                    }`}
                                                />
                                                {prefixAvailable !== null && (
                                                    <span className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                                                        prefixAvailable ? 'text-[#2D6A4F]' : 'text-[#D90429]'
                                                    }`}>
                                                        {prefixAvailable ? <Check size={18} /> : <X size={18} />}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-[#4B5878]">
                                                Este código aparecerá en los folios: {formData.code_prefix || 'XX'}-260327-0001
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Destino *</Label>
                                            <Input
                                                value={formData.destination}
                                                onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                                                placeholder="Riviera Maya, México"
                                                className="h-11 rounded-xl border-[#ECE3D2]"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Categoría del Hotel</Label>
                                            <Input
                                                value={formData.hotel_category}
                                                onChange={(e) => setFormData({ ...formData, hotel_category: e.target.value })}
                                                placeholder="4 Estrellas, 5 Estrellas Plus..."
                                                className="h-11 rounded-xl border-[#ECE3D2]"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label>Noches</Label>
                                            <Input
                                                type="number"
                                                min="1"
                                                value={formData.nights}
                                                onChange={(e) => setFormData({ ...formData, nights: e.target.value })}
                                                className="h-11 rounded-xl border-[#ECE3D2]"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Precio de Venta (MXN) *</Label>
                                            <Input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={formData.price}
                                                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                                placeholder="4999"
                                                data-testid="product-price-input"
                                                className="h-11 rounded-xl border-[#ECE3D2]"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Precio Original (tachado)</Label>
                                            <Input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={formData.original_price}
                                                onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                                                placeholder="8999"
                                                className="h-11 rounded-xl border-[#ECE3D2]"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Días de Validez</Label>
                                            <Input
                                                type="number"
                                                min="1"
                                                value={formData.validity_days}
                                                onChange={(e) => setFormData({ ...formData, validity_days: e.target.value })}
                                                className="h-11 rounded-xl border-[#ECE3D2]"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Orden de Visualización</Label>
                                            <Input
                                                type="number"
                                                min="0"
                                                value={formData.sort_order}
                                                onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                                                className="h-11 rounded-xl border-[#ECE3D2]"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center justify-between p-4 bg-[#F8F4EC] rounded-xl">
                                        <Label>Producto Activo (visible en POS)</Label>
                                        <Switch
                                            checked={formData.is_active}
                                            onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                        />
                                    </div>
                                </TabsContent>
                                
                                {/* Content Tab */}
                                <TabsContent value="content" className="space-y-4 mt-0">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Título del Certificado</Label>
                                            <Input
                                                value={formData.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                placeholder="Certificado de Hospedaje"
                                                className="h-11 rounded-xl border-[#ECE3D2]"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Subtítulo</Label>
                                            <Input
                                                value={formData.subtitle}
                                                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                                                placeholder="4 Noches Todo Incluido"
                                                className="h-11 rounded-xl border-[#ECE3D2]"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label>Descripción Breve (para POS)</Label>
                                        <RichTextEditor
                                            value={formData.brief_description}
                                            onChange={(v) => setFormData({ ...formData, brief_description: v })}
                                            placeholder="Disfruta de 4 noches todo incluido..."
                                            minHeight="80px"
                                            testId="brief-description-editor"
                                        />
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label>Descripción Completa</Label>
                                        <RichTextEditor
                                            value={formData.full_description}
                                            onChange={(v) => setFormData({ ...formData, full_description: v })}
                                            placeholder={"1. Tu certificado de viaje:\nEste certificado te regala 3 días y 2 noches..."}
                                            minHeight="160px"
                                            testId="full-description-editor"
                                            helperText="Selecciona texto y pulsa B / I / U para dar formato. Usa el color para resaltar."
                                        />
                                    </div>
                                    
                                    {/* Includes */}
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <Check size={16} className="text-[#2D6A4F]" />
                                            ¿Qué Incluye?
                                        </Label>
                                        <div className="flex gap-2">
                                            <Input
                                                value={newInclude}
                                                onChange={(e) => setNewInclude(e.target.value)}
                                                placeholder="Hospedaje, Alimentos, etc."
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInclude())}
                                                className="h-11 rounded-xl border-[#ECE3D2]"
                                            />
                                            <Button type="button" onClick={addInclude} variant="outline" className="h-11 px-4 rounded-xl">
                                                <Plus size={16} />
                                            </Button>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {formData.includes.map((item, i) => (
                                                <Badge key={i} className="bg-[#E8F5E9] text-[#2D6A4F] border-0 pr-1">
                                                    {item}
                                                    <button type="button" onClick={() => removeInclude(i)} className="ml-1 hover:text-[#D90429]">
                                                        <X size={14} />
                                                    </button>
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Excludes */}
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <X size={16} className="text-[#FB7185]" />
                                            ¿Qué NO Incluye?
                                        </Label>
                                        <div className="flex gap-2">
                                            <Input
                                                value={newExclude}
                                                onChange={(e) => setNewExclude(e.target.value)}
                                                placeholder="Vuelos, Propinas, etc."
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExclude())}
                                                className="h-11 rounded-xl border-[#ECE3D2]"
                                            />
                                            <Button type="button" onClick={addExclude} variant="outline" className="h-11 px-4 rounded-xl">
                                                <Plus size={16} />
                                            </Button>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {formData.excludes.map((item, i) => (
                                                <Badge key={i} className="bg-red-50 text-[#FB7185] border-0 pr-1">
                                                    {item}
                                                    <button type="button" onClick={() => removeExclude(i)} className="ml-1 hover:text-[#D90429]">
                                                        <X size={14} />
                                                    </button>
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label>Cómo Reservar</Label>
                                        <RichTextEditor
                                            value={formData.how_to_book}
                                            onChange={(v) => setFormData({ ...formData, how_to_book: v })}
                                            placeholder={"1. Llama a nuestro centro de reservaciones...\n2. Indica el folio..."}
                                            minHeight="140px"
                                            testId="how-to-book-editor"
                                        />
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label>Términos y Condiciones</Label>
                                        <RichTextEditor
                                            value={formData.terms_and_conditions}
                                            onChange={(v) => setFormData({ ...formData, terms_and_conditions: v })}
                                            placeholder={"- Válido por 365 días desde la compra\n- Sujeto a disponibilidad..."}
                                            minHeight="160px"
                                            testId="terms-editor"
                                        />
                                    </div>
                                </TabsContent>
                                
                                {/* Design Tab */}
                                <TabsContent value="design" className="space-y-4 mt-0">
                                    {/* Image */}
                                    <div className="space-y-3">
                                        <Label className="flex items-center gap-2">
                                            <Image size={16} />
                                            Imagen de Fondo
                                        </Label>

                                        {/* URL or pasted image */}
                                        <Input
                                            value={formData.design.background_image}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                design: { ...formData.design, background_image: e.target.value }
                                            })}
                                            placeholder="Pega una URL de imagen, o usa el generador IA debajo"
                                            data-testid="background-image-input"
                                            className="h-11 rounded-xl border-[#ECE3D2]"
                                        />

                                        {/* AI Generator Panel */}
                                        <div className="border border-[#ECE3D2] rounded-xl p-4 bg-[#FAF6F0]/40 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <MagicWand size={16} weight="duotone" className="text-[#FB7185]" />
                                                    <span className="text-sm font-semibold text-[#1F2D4B]">Generar con IA</span>
                                                    <span className="text-[10px] uppercase tracking-wider text-[#8B8474]">Nano Banana</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowGallery(v => !v)}
                                                    data-testid="toggle-gallery-btn"
                                                    className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md border border-[#ECE3D2] bg-white text-[#1F2D4B] hover:bg-[#F8F4EC]"
                                                >
                                                    <Images size={13} weight="bold" />
                                                    Galería {gallery.length > 0 && `(${gallery.length})`}
                                                </button>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between gap-2">
                                                    <Label className="text-xs text-[#4B5878]">
                                                        Describe la imagen exactamente como la quieres
                                                    </Label>
                                                    <button
                                                        type="button"
                                                        onClick={handleImprovePrompt}
                                                        disabled={improvingPrompt || !aiPrompt.trim()}
                                                        data-testid="improve-prompt-btn"
                                                        className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md bg-gradient-to-r from-[#FB7185] to-[#F472B6] text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm font-medium"
                                                    >
                                                        {improvingPrompt ? (
                                                            <SpinnerGap size={12} className="animate-spin" />
                                                        ) : (
                                                            <Sparkle size={12} weight="fill" />
                                                        )}
                                                        Mejorar prompt
                                                    </button>
                                                </div>
                                                <Textarea
                                                    value={aiPrompt}
                                                    onChange={(e) => setAiPrompt(e.target.value)}
                                                    placeholder='Ej: "Orlando bonito" o "Letrero de bienvenida a Orlando con la palabra ORLANDO en letras grandes coloridas"'
                                                    data-testid="ai-prompt-input"
                                                    rows={3}
                                                    className="rounded-md border-[#ECE3D2] text-sm"
                                                />
                                                <p className="text-[10px] text-[#8B8474]">
                                                    Tip: escribe lo que se te ocurra, aunque sea corto. Pulsa <b>Mejorar prompt</b> ✨ y la IA lo transforma en un prompt experto antes de generar.
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-2 gap-2">
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-[#4B5878]">Estilo visual</Label>
                                                    <Select value={aiStyle} onValueChange={setAiStyle}>
                                                        <SelectTrigger data-testid="ai-style-select" className="h-10 rounded-md border-[#ECE3D2] text-sm bg-white">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="photo">Fotografía realista</SelectItem>
                                                            <SelectItem value="cityscape">Ciudad / Skyline / Letreros</SelectItem>
                                                            <SelectItem value="aerial">Vista aérea / Dron</SelectItem>
                                                            <SelectItem value="cinematic">Cinematográfico</SelectItem>
                                                            <SelectItem value="illustration">Ilustración plana</SelectItem>
                                                            <SelectItem value="vector">Arte vectorial</SelectItem>
                                                            <SelectItem value="watercolor">Acuarela</SelectItem>
                                                            <SelectItem value="minimal">Minimalista</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-[#4B5878]">Evitar (opcional)</Label>
                                                    <Input
                                                        value={aiNegative}
                                                        onChange={(e) => setAiNegative(e.target.value)}
                                                        placeholder="playa, resort, palmeras"
                                                        data-testid="ai-negative-input"
                                                        className="h-10 rounded-md border-[#ECE3D2] text-sm bg-white"
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap gap-1.5">
                                                <span className="text-[10px] text-[#8B8474] mr-1 self-center">Inspiración:</span>
                                                {[
                                                    'Letras gigantes "ORLANDO" estilo Hollywood en una colina',
                                                    'Castillo de Disney al atardecer con fuegos artificiales',
                                                    'Skyline de Orlando visto desde lago Eola al amanecer',
                                                    'Cartel vintage "Welcome to Orlando" estilo años 50',
                                                ].map((sample, i) => (
                                                    <button
                                                        key={i}
                                                        type="button"
                                                        onClick={() => setAiPrompt(sample)}
                                                        className="text-[10px] px-2 py-1 rounded-full bg-white border border-[#ECE3D2] text-[#4B5878] hover:bg-[#F8F4EC]"
                                                    >
                                                        {sample.length > 38 ? sample.substring(0, 36) + '…' : sample}
                                                    </button>
                                                ))}
                                            </div>

                                            <Button
                                                type="button"
                                                onClick={handleGenerateImage}
                                                disabled={generatingImage || !aiPrompt.trim()}
                                                data-testid="generate-image-btn"
                                                className="w-full h-10 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md text-sm font-medium"
                                            >
                                                {generatingImage ? (
                                                    <>
                                                        <SpinnerGap size={16} className="animate-spin mr-2" />
                                                        Generando imagen (puede tardar 10–20s)…
                                                    </>
                                                ) : (
                                                    <>
                                                        <MagicWand size={16} className="mr-2" />
                                                        Generar imagen
                                                    </>
                                                )}
                                            </Button>

                                            {/* Gallery panel */}
                                            {showGallery && (
                                                <div className="border-t border-[#ECE3D2] pt-3" data-testid="gallery-panel">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-xs font-semibold text-[#1F2D4B] uppercase tracking-wider">Mis imágenes guardadas</span>
                                                        <span className="text-[10px] text-[#8B8474]">{gallery.length} imagen(es)</span>
                                                    </div>
                                                    {gallery.length === 0 ? (
                                                        <p className="text-[11px] text-[#8B8474] italic py-4 text-center">
                                                            Aún no tienes imágenes guardadas. Genera una y pulsa "Guardar para después".
                                                        </p>
                                                    ) : (
                                                        <div className="grid grid-cols-3 gap-2">
                                                            {gallery.map(item => (
                                                                <button
                                                                    key={item.id}
                                                                    type="button"
                                                                    onClick={() => handleUseFromGallery(item)}
                                                                    data-testid={`gallery-item-${item.id}`}
                                                                    title={item.prompt || 'Usar esta imagen'}
                                                                    className="relative group aspect-[3/4] rounded-md overflow-hidden border border-[#ECE3D2] hover:border-[#FB7185] transition-colors"
                                                                >
                                                                    <img
                                                                        src={item.image_data}
                                                                        alt={item.prompt || 'Galería'}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                    <span
                                                                        onClick={(e) => handleDeleteFromGallery(item.id, e)}
                                                                        className="absolute top-1 right-1 h-6 w-6 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-[#FB7185] opacity-0 group-hover:opacity-100 transition-opacity shadow-sm cursor-pointer"
                                                                    >
                                                                        <Trash size={11} weight="bold" />
                                                                    </span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {formData.design.background_image && (
                                            <div className="relative h-40 rounded-xl overflow-hidden border border-[#ECE3D2] group">
                                                <img
                                                    src={formData.design.background_image}
                                                    alt="Preview"
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    {formData.design.background_image.startsWith('data:') && (
                                                        <button
                                                            type="button"
                                                            onClick={handleSaveToGallery}
                                                            data-testid="save-to-gallery-btn"
                                                            className="h-8 px-3 bg-white/95 hover:bg-white rounded-md flex items-center gap-1 text-xs font-medium text-[#1F2D4B] shadow-sm"
                                                            title="Guardar para después en mi galería"
                                                        >
                                                            <FloppyDisk size={13} weight="duotone" /> Guardar
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            design: { ...formData.design, background_image: '' }
                                                        })}
                                                        data-testid="remove-bg-image-btn"
                                                        className="h-8 w-8 bg-white/95 hover:bg-white rounded-md flex items-center justify-center text-[#FB7185] shadow-sm"
                                                        title="Quitar imagen"
                                                    >
                                                        <Trash size={13} weight="bold" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Layout Style */}
                                    <div className="space-y-2">
                                        <Label>Estilo de Certificado</Label>
                                        <div className="grid grid-cols-3 gap-3">
                                            {LAYOUT_STYLES.map((style) => (
                                                <button
                                                    key={style.value}
                                                    type="button"
                                                    onClick={() => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, layout_style: style.value }
                                                    })}
                                                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                                                        formData.design.layout_style === style.value
                                                            ? 'border-[#1F2D4B] bg-[#FB7185]/5'
                                                            : 'border-[#ECE3D2] hover:border-[#1F2D4B]/50'
                                                    }`}
                                                >
                                                    <p className="font-medium text-[#1F2D4B] text-sm">{style.label}</p>
                                                    <p className="text-xs text-[#4B5878]">{style.desc}</p>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Colors */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-xs">Color Fondo</Label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="color"
                                                    value={formData.design.background_color}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, background_color: e.target.value }
                                                    })}
                                                    className="w-10 h-10 rounded-lg cursor-pointer"
                                                />
                                                <Input
                                                    value={formData.design.background_color}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, background_color: e.target.value }
                                                    })}
                                                    className="h-10 rounded-lg border-[#ECE3D2] font-mono text-xs flex-1"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs">Color Título</Label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="color"
                                                    value={formData.design.title_color}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, title_color: e.target.value }
                                                    })}
                                                    className="w-10 h-10 rounded-lg cursor-pointer"
                                                />
                                                <Input
                                                    value={formData.design.title_color}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, title_color: e.target.value }
                                                    })}
                                                    className="h-10 rounded-lg border-[#ECE3D2] font-mono text-xs flex-1"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs">Color Acento</Label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="color"
                                                    value={formData.design.accent_color}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, accent_color: e.target.value }
                                                    })}
                                                    className="w-10 h-10 rounded-lg cursor-pointer"
                                                />
                                                <Input
                                                    value={formData.design.accent_color}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, accent_color: e.target.value }
                                                    })}
                                                    className="h-10 rounded-lg border-[#ECE3D2] font-mono text-xs flex-1"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs">Color Badge</Label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="color"
                                                    value={formData.design.badge_color}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, badge_color: e.target.value }
                                                    })}
                                                    className="w-10 h-10 rounded-lg cursor-pointer"
                                                />
                                                <Input
                                                    value={formData.design.badge_color}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, badge_color: e.target.value }
                                                    })}
                                                    className="h-10 rounded-lg border-[#ECE3D2] font-mono text-xs flex-1"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Border style only — fonts moved to per-text panel below */}
                                    <div className="space-y-2">
                                        <Label>Estilo de Borde</Label>
                                        <Select
                                            value={formData.design.border_style}
                                            onValueChange={(value) => setFormData({
                                                ...formData,
                                                design: { ...formData.design, border_style: value }
                                            })}
                                        >
                                            <SelectTrigger className="h-11 rounded-xl border-[#ECE3D2]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {BORDER_STYLES.map((style) => (
                                                    <SelectItem key={style.value} value={style.value}>
                                                        <span className="font-medium">{style.label}</span>
                                                        <span className="text-xs text-[#8B8474] ml-2">— {style.desc}</span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Per-text Typography Panel */}
                                    <div className="pt-4 border-t border-[#ECE3D2]">
                                        <div className="flex items-center gap-2 mb-3">
                                            <TextAa size={16} className="text-[#1F2D4B]" />
                                            <Label className="text-sm font-semibold">Tipografía por texto</Label>
                                        </div>
                                        <p className="text-[11px] text-[#8B8474] mb-4">
                                            Personaliza fuente, peso, cursiva y tamaño para cada tipo de texto del certificado.
                                        </p>

                                        <div className="space-y-5">
                                            {[
                                                { key: 'title', label: 'Título principal', fontKey: 'title_font', sizeKey: 'title_size', weightKey: 'title_weight', italicKey: 'title_italic', min: 16, max: 48, hint: 'Ej. Escapada 4 Estrellas Riviera Maya' },
                                                { key: 'subtitle', label: 'Subtítulo', fontKey: 'subtitle_font', sizeKey: 'subtitle_size', weightKey: 'subtitle_weight', italicKey: 'subtitle_italic', min: 10, max: 24, hint: 'Línea secundaria descriptiva' },
                                                { key: 'body', label: 'Cuerpo', fontKey: 'body_font', sizeKey: 'body_size', weightKey: 'body_weight', italicKey: 'body_italic', min: 9, max: 18, hint: 'Descripciones, listas, instrucciones' },
                                                { key: 'label', label: 'Etiquetas', fontKey: 'label_font', sizeKey: 'label_size', weightKey: 'label_weight', italicKey: 'label_italic', min: 8, max: 14, hint: 'DESTINO, VIGENCIA, VALOR…' }
                                            ].map((cfg) => {
                                                const font = formData.design[cfg.fontKey] || (cfg.key === 'title' ? formData.design.title_font : formData.design.subtitle_font);
                                                const weight = formData.design[cfg.weightKey] ?? (cfg.key === 'title' || cfg.key === 'label' ? 700 : 400);
                                                const italic = !!formData.design[cfg.italicKey];
                                                const size = formData.design[cfg.sizeKey];
                                                const update = (patch) => setFormData({
                                                    ...formData,
                                                    design: { ...formData.design, ...patch }
                                                });
                                                return (
                                                    <div key={cfg.key} className="border border-[#ECE3D2] rounded-lg p-3.5 bg-[#FAF6F0]/40" data-testid={`typography-${cfg.key}`}>
                                                        <div className="flex items-baseline justify-between mb-2.5">
                                                            <div>
                                                                <p className="text-sm font-semibold text-[#1F2D4B]">{cfg.label}</p>
                                                                <p className="text-[10px] text-[#8B8474]">{cfg.hint}</p>
                                                            </div>
                                                            <span className="text-[10px] uppercase tracking-wider text-[#8B8474]" style={{ fontFamily: font, fontWeight: weight, fontStyle: italic ? 'italic' : 'normal' }}>
                                                                Aa
                                                            </span>
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto] gap-2 mb-3">
                                                            <Select
                                                                value={font}
                                                                onValueChange={(v) => update({ [cfg.fontKey]: v })}
                                                            >
                                                                <SelectTrigger data-testid={`${cfg.key}-font-select`} className="h-9 rounded-md border-[#ECE3D2] text-xs bg-white">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {FONT_OPTIONS.map((font) => (
                                                                        <SelectItem key={font.value} value={font.value}>
                                                                            <span style={{ fontFamily: font.value }}>{font.label}</span>
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>

                                                            <Select
                                                                value={String(weight)}
                                                                onValueChange={(v) => update({ [cfg.weightKey]: parseInt(v) })}
                                                            >
                                                                <SelectTrigger data-testid={`${cfg.key}-weight-select`} className="h-9 rounded-md border-[#ECE3D2] text-xs bg-white">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {WEIGHT_OPTIONS.map((w) => (
                                                                        <SelectItem key={w.value} value={String(w.value)}>{w.label}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>

                                                            <div className="flex gap-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => update({ [cfg.weightKey]: weight >= 700 ? 400 : 700 })}
                                                                    data-testid={`${cfg.key}-bold-toggle`}
                                                                    title="Negrita rápida"
                                                                    className={`h-9 w-9 rounded-md border text-sm font-bold transition-colors ${weight >= 700 ? 'bg-[#1F2D4B] text-white border-[#1F2D4B]' : 'bg-white text-[#1F2D4B] border-[#ECE3D2] hover:bg-[#F8F4EC]'}`}
                                                                >
                                                                    B
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => update({ [cfg.italicKey]: !italic })}
                                                                    data-testid={`${cfg.key}-italic-toggle`}
                                                                    title="Cursiva"
                                                                    className={`h-9 w-9 rounded-md border text-sm italic font-serif transition-colors ${italic ? 'bg-[#1F2D4B] text-white border-[#1F2D4B]' : 'bg-white text-[#1F2D4B] border-[#ECE3D2] hover:bg-[#F8F4EC]'}`}
                                                                >
                                                                    I
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <div className="flex items-center justify-between mb-1.5">
                                                                <Label className="text-[11px] text-[#4B5878]">Tamaño</Label>
                                                                <span className="text-[11px] font-mono-brand font-semibold text-[#1F2D4B]">{size}px</span>
                                                            </div>
                                                            <Slider
                                                                min={cfg.min}
                                                                max={cfg.max}
                                                                step={1}
                                                                value={[size]}
                                                                onValueChange={(v) => update({ [cfg.sizeKey]: v[0] })}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => setFormData({
                                                ...formData,
                                                design: {
                                                    ...formData.design,
                                                    title_font: DEFAULT_DESIGN.title_font,
                                                    subtitle_font: DEFAULT_DESIGN.subtitle_font,
                                                    body_font: DEFAULT_DESIGN.body_font,
                                                    label_font: DEFAULT_DESIGN.label_font,
                                                    title_weight: DEFAULT_DESIGN.title_weight,
                                                    subtitle_weight: DEFAULT_DESIGN.subtitle_weight,
                                                    body_weight: DEFAULT_DESIGN.body_weight,
                                                    label_weight: DEFAULT_DESIGN.label_weight,
                                                    title_italic: false,
                                                    subtitle_italic: false,
                                                    body_italic: false,
                                                    label_italic: false,
                                                    title_size: 28,
                                                    subtitle_size: 14,
                                                    body_size: 12,
                                                    label_size: 10
                                                }
                                            })}
                                            data-testid="reset-typography-btn"
                                            className="mt-3 h-8 text-xs rounded-md border-[#ECE3D2] text-[#4B5878]"
                                        >
                                            Restablecer tipografía
                                        </Button>
                                    </div>
                                </TabsContent>
                            </div>
                        </Tabs>
                        </div>

                        {/* RIGHT: Live Preview Panel */}
                        <div className="flex flex-col bg-[#FAF6F0] border-t md:border-t-0 md:border-l border-[#ECE3D2] overflow-y-auto min-h-0 min-w-0">
                            <div className="sticky top-0 bg-[#FAF6F0] z-10 px-5 py-4 border-b border-[#ECE3D2]">
                                <div className="flex items-center gap-2">
                                    <Eye size={14} className="text-[#1F2D4B]" />
                                    <p className="text-xs font-semibold uppercase tracking-wider text-[#1F2D4B]">
                                        Vista previa en vivo
                                    </p>
                                </div>
                                <p className="text-[10px] text-[#8B8474] mt-1">Así lo recibirá tu cliente · scroll para ver todo</p>
                            </div>
                            <div className="p-5 pb-8 flex-1">
                                <CertificatePreview data={formData} />
                            </div>
                        </div>
                    </form>
                    
                    {/* Footer */}
                    <div className="flex gap-2 p-6 pt-4 border-t border-[#ECE3D2] bg-white">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowModal(false)}
                            data-testid="cancel-product-btn"
                            className="flex-1 h-10 rounded-md border-[#ECE3D2] text-[#1F2D4B] hover:bg-[#F8F4EC] text-sm font-medium"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            form="product-form"
                            data-testid="save-product-btn"
                            className="flex-1 h-10 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md text-sm font-medium btn-lift"
                        >
                            {editingProduct ? 'Guardar cambios' : 'Crear producto'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ProductsPage;
